import React, { lazy } from "react";
import { Navigate } from "react-router-dom";

// Route-scoped Context provider'ları (Faz 5'te React Query hook'larıyla yer değiştirecek)
import UserProvider from "../_helper/User/UserProvider";
import TodoProvider from "../_helper/Todo/TodoProvider";
import TaskProvider from "../_helper/Task/TaskProvider";
import ImportProvider from "../_helper/Import/ImportProvider";
import ClientProvider from "../_helper/Client/ClientProvider";
import TicketsProvider from "../_helper/Tickets/TicketsProvider";
import ProjectProvider from "../_helper/Project/ProjectProvider";

// Tek satırlık fonksiyon — legacy `/default/:layout` deep-link'leri
// kırılmasın diye `/` redirect component'i.
const RootRedirect = () => <Navigate to="/" replace />;

/**
 * Tüm uygulama route tanımları.
 *
 * - Component'ler `React.lazy` ile yüklenir; route bazlı code splitting bundle'ı
 *   küçültür ve initial TTI'yi düşürür.
 * - `:layout` URL parametresi tarihsel sebeple korunuyor (12 şehir tema sistemi);
 *   Faz 4 (tema konsolidasyonu) bu segmenti URL'den tamamen kaldıracak.
 * - Provider'lar `wrapWithProviders` ile compose edilir; her route kendi feature
 *   provider'larını listeleyebilir.
 */

// ===== Dashboards & main pages =====
const Default = lazy(() => import("../Components/Application/Default"));
const HR = lazy(() => import("../Components/Application/HR"));
const SalesScreen = lazy(() => import("../Components/Application/SalesScreen"));
const GoogleMeet = lazy(() => import("../Components/Application/GoogleMeet"));
const ZoomMeet = lazy(() => import("../Components/Application/ZoomMeet"));
const Notes = lazy(() => import("../Components/Application/Notes"));
const Notification = lazy(() => import("../Components/Application/Notification"));
const SettingsWrapper = lazy(() => import("../Components/Application/Settings"));
const Files = lazy(() => import("../Components/Application/Files"));
const Expenses = lazy(() => import("../Components/Application/Expenses"));
const Report = lazy(() => import("../Components/Application/Reports"));

// ===== Team =====
const TeamMembers = lazy(() => import("../Components/Application/Team/TeamMembers"));
const Leave = lazy(() => import("../Components/Application/Team/Leave"));
const MyLeave = lazy(() => import("../Components/Application/Team/MyLeave"));
const Timeline = lazy(() => import("../Components/Application/Team/Timeline"));
const Announcements = lazy(() => import("../Components/Application/Team/Announcements"));
const AnnouncementsDetail = lazy(() =>
  import("../Components/Application/Team/Announcements/AnnouncementsDetail")
);

// ===== Tickets =====
const Tickets = lazy(() => import("../Components/Application/Tickets"));
const TicketsDetail = lazy(() =>
  import("../Components/Application/Tickets/TicketsDetail")
);

// ===== Help & Support =====
const HelpSupport = lazy(() => import("../Components/Application/HelpSupport/Help"));
const CategoryDetail = lazy(() =>
  import("../Components/Application/HelpSupport/Help/Category")
);
const ViewPage = lazy(() =>
  import("../Components/Application/HelpSupport/Help/View")
);
const HelpArticle = lazy(() =>
  import("../Components/Application/HelpSupport/HelpArticles")
);
const HelpCategories = lazy(() =>
  import("../Components/Application/HelpSupport/HelpCategories")
);
const KnowledgeBase = lazy(() =>
  import("../Components/Application/HelpSupport/KnowledgeBase")
);
const KnowledgeCategory = lazy(() =>
  import("../Components/Application/HelpSupport/KnowledgeBase/KnowledgeCategory")
);
const KnowledgeView = lazy(() =>
  import("../Components/Application/HelpSupport/KnowledgeBase/KnowledgeView")
);
const KBArticle = lazy(() =>
  import("../Components/Application/HelpSupport/KBArticles")
);
const KBCategories = lazy(() =>
  import("../Components/Application/HelpSupport/KBCategories")
);

// ===== Clients & Leads =====
const Clients = lazy(() => import("../Components/Application/Clients"));
const ClientsView = lazy(() =>
  import("../Components/Application/Clients/ClientsView")
);
const Leads = lazy(() => import("../Components/Application/Leads"));
const LeadsDetailPage = lazy(() =>
  import("../Components/Application/Leads/LeadsDetailPage")
);
const UsersProfileContain = lazy(() =>
  import("../Components/Application/Users/UsersProfile")
);

// ===== Sales =====
const Invoices = lazy(() => import("../Components/Application/Sales/Invoices"));
const InvoicesDetail = lazy(() =>
  import("../Components/Application/Sales/Invoices/InvoicesDetail")
);
const Payments = lazy(() => import("../Components/Application/Sales/Payments"));
const Items = lazy(() => import("../Components/Application/Sales/Items"));
const Contracts = lazy(() => import("../Components/Application/Sales/Contracts"));
const ContractDetail = lazy(() =>
  import("../Components/Application/Sales/Contracts/ContractDetail")
);
const OrderList = lazy(() => import("../Components/Application/Sales/OrderList"));
const OrdersDetail = lazy(() =>
  import("../Components/Application/Sales/OrderList/OrdersDetail")
);
const Store = lazy(() => import("../Components/Application/Sales/Store"));
const ProductDetailPage = lazy(() =>
  import("../Components/Application/Sales/Store/ProductDetailPage")
);
const ProcessOrder = lazy(() =>
  import("../Components/Application/Sales/Store/ProcessOrder")
);
const ContractRequest = lazy(() =>
  import("../Components/Application/Sales/ContractRequest")
);

// ===== Prospects =====
const EstimateList = lazy(() =>
  import("../Components/Application/Prospects/EstimateList")
);
const EstimateRequest = lazy(() =>
  import("../Components/Application/Prospects/EstimateRequest")
);
const EstimateForms = lazy(() =>
  import("../Components/Application/Prospects/EstimateForms")
);
const Proposals = lazy(() => import("../Components/Application/Prospects/Proposals"));
const ProposalDetail = lazy(() =>
  import("../Components/Application/Prospects/Proposals/ProposalDetail")
);

// ===== Tasks =====
const Task = lazy(() => import("../Components/Application/Task"));
const TaskInfoPage = lazy(() =>
  import("../Components/Application/Task/TaskInfoPage")
);

// ===== Projects =====
const Project = lazy(() => import("../Components/Application/Project/Project"));
const Newproject = lazy(() =>
  import("../Components/Application/Project/Newproject")
);
const ProjectDetail = lazy(() =>
  import("../Components/Application/Project/ProjectDetail")
);

// ===== Calendar =====
const CalendarPage = lazy(() => import("../features/calendar/pages/CalendarPage"));

const PUB = "";

export const routes = [
  // Legacy `/*` redirects — preserve old bookmarks/links
  { path: `${PUB}/dashboard`, Component: RootRedirect },
  { path: `${PUB}/*`, Component: RootRedirect },

  // ========== Dashboard ==========
  // Yeni anasayfa: kök path. v6 nested <Routes> içinde "kök" olarak `index: true`
  // kullanılır (path "/" değil — nested route resolution'da sorun çıkarıyor).
  // Tüm legacy /default/:layout deep-link'leri RootRedirect ile
  // `/`'a yönlenir; deep-link uyumluluğu korunur.
  { index: true, Component: Default,
    providers: [UserProvider, TodoProvider, TaskProvider, ImportProvider] },
  { path: `${PUB}/default/:layout`, Component: RootRedirect },
  { path: `${PUB}/hr/:layout`, Component: HR,
    providers: [UserProvider, ImportProvider] },
  { path: `${PUB}/sales-screen/:layout`, Component: SalesScreen,
    providers: [UserProvider, ImportProvider] },
  { path: `${PUB}/google-meet/:layout`, Component: GoogleMeet,
    providers: [UserProvider, ImportProvider] },
  { path: `${PUB}/zoom-meet/:layout`, Component: ZoomMeet,
    providers: [UserProvider, ImportProvider] },

  // ========== Clients ==========
  { path: `${PUB}/clients/:layout`, Component: Clients,
    providers: [UserProvider, ImportProvider, ClientProvider] },
  { path: `${PUB}/clients/view/:id/:layout`, Component: ClientsView,
    providers: [TaskProvider, UserProvider, TicketsProvider, ImportProvider, ClientProvider] },
  { path: `${PUB}/clients/contact_profile/:id/:layout`,
    Component: UsersProfileContain, providers: [UserProvider, ImportProvider] },

  // ========== Notes ==========
  { path: `${PUB}/notes/:layout`, Component: Notes,
    providers: [ImportProvider] },

  // ========== Team ==========
  { path: `${PUB}/team/:layout`, Component: TeamMembers,
    providers: [ImportProvider] },
  { path: `${PUB}/leave/:layout`, Component: Leave,
    providers: [ImportProvider] },
  { path: `${PUB}/my-leave/:layout`, Component: MyLeave,
    providers: [ImportProvider] },
  { path: `${PUB}/timeLine/:layout`, Component: Timeline,
    providers: [UserProvider] },
  { path: `${PUB}/announcements/:layout`, Component: Announcements,
    providers: [ImportProvider] },
  { path: `${PUB}/announcements/view/:id/:layout`,
    Component: AnnouncementsDetail },

  // ========== Tickets ==========
  { path: `${PUB}/tickets/:layout`, Component: Tickets,
    providers: [TicketsProvider, ImportProvider] },
  { path: `${PUB}/tickets/tickets-detail/:id/:layout`,
    Component: TicketsDetail,
    providers: [TicketsProvider, TaskProvider, ImportProvider] },

  // ========== Misc ==========
  { path: `${PUB}/expenses/:layout`, Component: Expenses,
    providers: [ImportProvider] },
  { path: `${PUB}/reports/:layout`, Component: Report,
    providers: [ImportProvider] },
  { path: `${PUB}/files/:layout`, Component: Files },

  // ========== Help & Support ==========
  { path: `${PUB}/help/:layout`, Component: HelpSupport,
    providers: [ImportProvider] },
  { path: `${PUB}/help/category/:id/:layout`, Component: CategoryDetail,
    providers: [ImportProvider] },
  { path: `${PUB}/help/view/:id/:layout`, Component: ViewPage,
    providers: [ImportProvider] },
  { path: `${PUB}/help/help-articles/:layout`, Component: HelpArticle,
    providers: [ImportProvider] },
  { path: `${PUB}/help/help-categories/:layout`, Component: HelpCategories,
    providers: [ImportProvider] },
  { path: `${PUB}/help/knowledge-base/:layout`, Component: KnowledgeBase,
    providers: [ImportProvider] },
  { path: `${PUB}/help/knowledge-base/category/:id/:layout`,
    Component: KnowledgeCategory, providers: [ImportProvider] },
  { path: `${PUB}/help/knowledge-base/view/:id/:layout`,
    Component: KnowledgeView, providers: [ImportProvider] },
  { path: `${PUB}/help/knowledge-base-articles/:layout`,
    Component: KBArticle, providers: [ImportProvider] },
  { path: `${PUB}/help/knowledge-base-categories/:layout`,
    Component: KBCategories, providers: [ImportProvider] },

  // ========== Settings ==========
  { path: `${PUB}/settings/:layout`, Component: SettingsWrapper },

  // ========== Leads ==========
  { path: `${PUB}/leads/:layout`, Component: Leads,
    providers: [UserProvider, ImportProvider] },
  { path: `${PUB}/leads/leads-detail/:id/:layout`,
    Component: LeadsDetailPage,
    providers: [TaskProvider, UserProvider, ImportProvider] },
  { path: `${PUB}/leads/contact_profile/:id/:layout`,
    Component: UsersProfileContain, providers: [UserProvider, ImportProvider] },

  // ========== Sales ==========
  { path: `${PUB}/invoices/:layout`, Component: Invoices,
    providers: [ImportProvider] },
  { path: `${PUB}/invoices/view/:id/:layout`, Component: InvoicesDetail,
    providers: [TaskProvider, ImportProvider] },
  { path: `${PUB}/payments/:layout`, Component: Payments,
    providers: [ImportProvider] },
  { path: `${PUB}/items/:layout`, Component: Items,
    providers: [ImportProvider] },
  { path: `${PUB}/contracts/:layout`, Component: Contracts,
    providers: [ImportProvider] },
  { path: `${PUB}/contracts/view/:id/:layout`, Component: ContractDetail,
    providers: [TaskProvider, ImportProvider] },
  { path: `${PUB}/contracts/preview/:id/:layout`, Component: ContractDetail,
    providers: [TaskProvider, ImportProvider] },
  { path: `${PUB}/orders/:layout`, Component: OrderList,
    providers: [ImportProvider] },
  { path: `${PUB}/orders/view/:id/:layout`, Component: OrdersDetail,
    providers: [TaskProvider, ImportProvider] },
  { path: `${PUB}/store`, Component: Store },
  { path: `${PUB}/store/:id`, Component: ProductDetailPage },
  { path: `${PUB}/store/checkout`, Component: ProcessOrder },
  { path: `${PUB}/contract-requests/:layout`, Component: ContractRequest,
    providers: [ImportProvider] },

  // ========== Prospects ==========
  { path: `${PUB}/estimates/:layout`, Component: EstimateList,
    providers: [ImportProvider] },
  { path: `${PUB}/estimate-requests/:layout`, Component: EstimateRequest,
    providers: [ImportProvider] },
  { path: `${PUB}/estimate-forms/:layout`, Component: EstimateForms,
    providers: [ImportProvider] },
  { path: `${PUB}/proposals/:layout`, Component: Proposals,
    providers: [ImportProvider] },
  { path: `${PUB}/proposals/view/:id/:layout`, Component: ProposalDetail,
    providers: [TaskProvider, ImportProvider] },

  // ========== Tasks ==========
  { path: `${PUB}/task/:layout`, Component: Task,
    providers: [TaskProvider, ImportProvider] },
  { path: `${PUB}/tasks/view/:id`, Component: TaskInfoPage,
    providers: [TaskProvider, ImportProvider] },

  // ========== Projects ==========
  { path: `${PUB}/projects/:layout`, Component: Project,
    providers: [ProjectProvider, ImportProvider] },
  { path: `${PUB}/projects/project-detail/:id/:layout`,
    Component: ProjectDetail,
    providers: [TaskProvider, TicketsProvider, ImportProvider] },
  { path: `${PUB}/projects/new-project/:layout`, Component: Newproject,
    providers: [ProjectProvider] },

  // ========== Calendar / Notifications / User profile ==========
  { path: `${PUB}/calendar/draggable-calendar/:layout`,
    Component: CalendarPage,
    providers: [TaskProvider, ImportProvider] },
  { path: `${PUB}/notifications/:layout`, Component: Notification },
  { path: `${PUB}/users/profile/:id/:layout`,
    Component: UsersProfileContain, providers: [UserProvider, ImportProvider] },
];
